export {
  appSurfaceValues,
  DEFAULT_CHAT_TITLE,
  embeddedSurfaces,
  feedbackCategoryValues,
  feedbackStatusValues,
  feedbackTypeValues,
  feedbackVoteValues,
  isEmbeddedSurface,
  isOfficeAddin,
  officeAddinSurfaces,
  SURFACE_LABELS,
  surfaceLabel,
} from "@magnus/contract/values";
// Re-exported here so the web app can ask what an unnamed creation is called
// without reaching into `packages/features` for it.
export { DEFAULT_CREATE_PROJECT_NAME } from "@workspace/ai/capabilities/create/constants";
export type { Announcement } from "@workspace/database/schema/announcements";
export type { Chat, Workspace } from "@workspace/database/schema/chat";
export type {
  AppSurface,
  EmbeddedSurface,
  FeedbackCategory,
  FeedbackStatus,
  FeedbackType,
  FeedbackVote,
  OfficeAddinSurface,
} from "@workspace/database/schema/chat/schema";
export type {
  AuditStatus,
  Skill,
  SkillEvent,
  SkillRevision,
  SkillTarget,
  SkillUserOverride,
} from "@workspace/database/schema/skill";

import type { Skill } from "@workspace/database/schema/skill";

/**
 * Pure predicate: "is this skill visible to anyone in the org?"
 *
 * Mirrors the SQL filter used by the server-side `listPublic` repo
 * method (`visibility = 'public' AND auditStatus = 'passed'`) and the
 * official-public branch in `isAccessibleToUser`. Official skills no
 * longer get a free pass on visibility — a private official skill is
 * staged content visible only to admins, so it is *not* public-visible.
 *
 * Pure / no DB or session dependencies — safe to call from client
 * components (e.g. tab-filter logic on the agents page) so client and
 * server stay in lockstep.
 */
export function isSkillPublicVisible(
  s: Pick<Skill, "isOfficial" | "visibility" | "auditStatus">
): boolean {
  if (s.isOfficial) {
    return s.visibility === "public";
  }
  return s.visibility === "public" && s.auditStatus === "passed";
}

/**
 * Pick the revision id to load for a given viewing perspective.
 *
 * - `"published"` → live version (publishedRevisionId), falling back to
 *   the latest draft only when no publish exists yet (brand-new skill).
 *   This is what non-managers see and what we render on the public
 *   detail page.
 * - `"draft"` → the editor view: latest revision, falling back to
 *   published if a brand-new skill has no draft pointer yet. Managers
 *   editing an unpublished skill land here.
 *
 * Centralizes the `??` fallback order — flipping the sides is an easy
 * mistake when transcribing inline at each call site, and any single
 * skill where the two pointers happen to match would mask the bug.
 */
export function resolveRevisionId(
  s: Pick<Skill, "publishedRevisionId" | "latestRevisionId">,
  prefer: "published" | "draft"
): string | null {
  if (prefer === "published") {
    return s.publishedRevisionId ?? s.latestRevisionId ?? null;
  }
  return s.latestRevisionId ?? s.publishedRevisionId ?? null;
}

/**
 * A user ID that has been verified by an auth boundary (session middleware).
 *
 * Use this instead of plain `string` for any service method that should only
 * operate on the currently authenticated user. The brand prevents accidental
 * use of user-supplied input (e.g. `input.userId`) where only the session
 * user is allowed — the compiler will reject it.
 *
 * Only call `asAuthenticatedUser()` at auth boundaries — never in route
 * handlers or service code.
 */
export type AuthenticatedUserId = string & {
  readonly __brand: "AuthenticatedUserId";
};

/**
 * Cast a session-verified user ID to `AuthenticatedUserId`.
 *
 * ONLY call this inside auth middleware / session verification code.
 */
export function asAuthenticatedUser(id: string): AuthenticatedUserId {
  return id as AuthenticatedUserId;
}

/**
 * Sentinel for service calls made on behalf of the system itself rather
 * than an end user — e.g. `SkillPublisher` submitting committed files
 * to the Docling document-processing pipeline (ADR-055), pipeline
 * retry sweeps, audit workflows. The phantom `__system` brand makes
 * these calls greppable and prevents the value from being confused with
 * a real user id at the type level.
 */
export type SystemUserId = AuthenticatedUserId & { readonly __system: true };

/**
 * Construct the privileged system actor. ONLY use this from system
 * contexts where bypassing user-scoping is a deliberate design choice
 * (publisher pipelines, scheduled cleanup, callback handlers). Per
 * ADR-017, replacing this factory is preferred over raw `as
 * AuthenticatedUserId` casts because grep tracks every system actor in
 * the codebase to a single declaration.
 */
export function asSystemUser(): SystemUserId {
  return "system" as SystemUserId;
}
